// DOM HUD, panels, toasts, guide overlay, touch controls, night tint, dev overlay.
// Reads game state one-way; dispatches via game methods. (TECHNICAL_DESIGN.md §11.)
import { BLESSINGS, BUILD, FEEDBACK_URL } from './const';
import type { Game } from './game';
import { ITEMS, RECIPES, canCraft } from './items';
import { MOBS } from './entities';
import { DUNGEON_MAX_FLOOR, bossKindForFloor, bossName } from './dungeon';
import { LEGENDARIES, MOTIF_NAMES, RARITY_NAMES, RELIC_EFFECTS, RELIC_ICON, SLOTS, SLOT_ICON, SLOT_LABEL, empoweredBase, relicPower } from './loot';
import type { Sfx } from './sfx';
import { PALETTE } from './textures';
import type { AccountApi, AffixId, GearItem, RelicItem, Slot } from './types';

const el = (tag: string, cls?: string, html?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html != null) e.innerHTML = html;
  return e;
};

// Player-facing changelog, newest first — rendered as the "What's new" section of the Guide tab.
// Keep entries short and in the player's language; bump alongside BUILD in const.ts.
const CHANGELOG: { v: string; date: string; notes: string[] }[] = [
  {
    v: 'Beta v0.1.0', date: '2026-07-12', notes: [
      'First public beta — welcome to the grove! 🌿',
      'Equip loot in the new <b>Hero</b> panel: 6 gear slots + 2 relics, with rarities and set previews.',
      'Dungeons are a finite <b>10-floor</b> run with three distinct bosses — Warden Acolyte, Bone Warden, and <b>The Hollow King</b>.',
      'Gentle onboarding, a 📖 Guide tab, and juicier drops, level-ups and boss kills.',
      'New <b>Collection</b> log in the Guide — fill in creatures, relics and recipes as you find them.',
      'Restore the <b>Grove</b>: carry grove relics from the Hollow King home and light the totems — colour returns to the world and you earn lasting boons.',
      'Found a bug or have an idea? Tap <b>💬 Feedback</b> in ⚙️ Settings.',
    ],
  },
];

export function mountUI(root: HTMLElement, game: Game, sfx?: Sfx, account?: AccountApi): void {
  root.innerHTML = '';
  injectStyles();

  // --- night tint (screen-space) ---
  const night = el('div', 'wg-night');
  root.appendChild(night);

  // --- dungeon fog: darkness with only a small circle of torchlight around the player ---
  const fog = el('div', 'wg-fog wg-hidden');
  root.appendChild(fog);

  // --- HUD ---
  const hud = el('div', 'wg-hud');
  const hpWrap = el('div', 'wg-bar-wrap');
  const hpFill = el('div', 'wg-bar wg-hp');
  hpWrap.appendChild(hpFill);
  const hpText = el('div', 'wg-bar-text');
  hpWrap.appendChild(hpText);
  const gold = el('div', 'wg-gold', '🪙 0');
  const foodRow = el('div', 'wg-food');
  const weaponEl = el('div', 'wg-weapon clickable', '✊ Fists');
  weaponEl.title = 'Click (or press 1/2) to switch weapon';
  weaponEl.onclick = () => { game.setWeapon(game.weapon === 'melee' ? 'bow' : 'melee'); };
  const safeBadge = el('div', 'wg-safe wg-hidden', '🛡️ Safe zone');
  hud.append(hpWrap, foodRow, gold, weaponEl, safeBadge);
  root.appendChild(hud);

  // --- top-right cluster: sun/moon clock + compass + satchel ---
  const cluster = el('div', 'wg-tr clickable');
  const clockW = el('div', 'wg-widget');
  clockW.innerHTML =
    '<div class="wg-clock"><div class="wg-clock-horizon"></div><div class="wg-orb">☀️</div></div>' +
    '<div class="wg-cap wg-clock-lbl">Day</div>';
  const compW = el('div', 'wg-widget');
  compW.innerHTML =
    '<div class="wg-compass"><span class="wg-cn">N</span><span class="wg-ce">E</span><span class="wg-cs">S</span><span class="wg-cw">W</span><div class="wg-comp-arrow">▲</div></div>' +
    '<div class="wg-cap wg-comp-lbl">home</div>';
  cluster.append(clockW, compW);
  root.appendChild(cluster);
  const clock = clockW.querySelector('.wg-clock') as HTMLElement;
  const orb = clockW.querySelector('.wg-orb') as HTMLElement;
  const clockLbl = clockW.querySelector('.wg-clock-lbl') as HTMLElement;
  const compArrow = compW.querySelector('.wg-comp-arrow') as HTMLElement;
  const compLbl = compW.querySelector('.wg-comp-lbl') as HTMLElement;

  // --- goal hint (top-center) ---
  const goal = el('div', 'wg-goal');
  root.appendChild(goal);

  // --- contextual hint (bottom-center) ---
  const hint = el('div', 'wg-hint');
  root.appendChild(hint);

  // --- toasts ---
  const toasts = el('div', 'wg-toasts');
  root.appendChild(toasts);

  // ================= UNIFIED CONSOLE: Bag · Craft · Delve · Settings =================
  const RARITY_HEX = ['#9a9a9a', '#5ac85a', '#4a90e2', '#b060e0', '#f0a030'];
  const MOTIF_ICON: Record<string, string> = { wolf: '🐺', ember: '🔥', root: '🌿' };

  const menuBtn = el('button', 'wg-btn wg-menu-btn clickable', '🎒');
  root.appendChild(menuBtn);
  const con = el('div', 'wg-console wg-hidden clickable');
  con.innerHTML =
    '<div class="wg-con-head"><div class="wg-con-tabs">'
    + '<button class="wg-con-tab wg-tab-hero-btn clickable" data-tab="hero"><canvas class="wg-tabface" width="48" height="48"></canvas><span class="wg-hero-name">Hero</span></button>'
    + '<button class="wg-con-tab clickable" data-tab="bag">🎒 Bag</button>'
    + '<button class="wg-con-tab clickable" data-tab="craft">🔨 Craft</button>'
    + '<button class="wg-con-tab clickable" data-tab="delve">⛓️ Dungeon</button>'
    + '<button class="wg-con-tab clickable" data-tab="guides">📖 Guide<span class="wg-cl-badge wg-hidden">•</span></button>'
    + '<button class="wg-con-tab clickable" data-tab="settings">⚙️</button>'
    + '</div><button class="wg-con-close clickable">✕</button></div>'
    + '<div class="wg-con-body">'
    + '<div class="wg-tab wg-tab-hero">'
    + '<div class="wg-hero-top"><div class="wg-doll"><canvas class="wg-doll-face" width="168" height="150"></canvas></div>'
    + '<div class="wg-stats"></div></div>'
    + '<div class="wg-slotgrid"></div>'
    + '<div class="wg-relicrow"></div>'
    + '<div class="wg-passives"></div>'
    + '<div class="wg-grove"></div>'
    + '<div class="wg-picker wg-hidden"></div>'
    + '<div class="wg-gear-foot"><button class="wg-salvage clickable">♻️ Salvage &lt; Rare</button></div></div>'
    + '<div class="wg-tab wg-tab-bag wg-hidden"><div class="wg-inv-grid"></div></div>'
    + '<div class="wg-tab wg-tab-craft wg-hidden"><div class="wg-near"></div><div class="wg-cat-chips"></div><div class="wg-craft-list"></div></div>'
    + '<div class="wg-tab wg-tab-delve wg-hidden"></div>'
    + '<div class="wg-tab wg-tab-guides wg-hidden"></div>'
    + '<div class="wg-tab wg-tab-settings wg-hidden">'
    + `<div class="wg-set-row"><span class="wg-ver">${BUILD}</span><button class="wg-feedback clickable">💬 Feedback (30 sec)</button></div>`
    + '<div class="wg-account wg-hidden"></div>'
    + '<div class="wg-set-note">Anonymous, cookieless play counts help me improve the grove — no accounts, no tracking across sites.</div>'
    + '<div class="wg-set-row"><span>Sound</span><button class="wg-sound clickable"></button></div>'
    + '<div class="wg-set-row"><span class="wg-sanct-lbl">Sanctuary</span><button class="wg-sanct clickable"></button></div>'
    + '<div class="wg-set-note">A safe ring around your home: no monsters spawn inside and nothing can hurt you — creatures that wander in are banished. Expand it to widen the protected area.</div>'
    + '<div class="wg-set-row"><span>Share world</span><button class="wg-share clickable">🔗 Copy link</button></div>'
    + '<div class="wg-set-note">Your progress lives in this browser. On iOS, install to Home Screen or keep a save code so it survives.</div>'
    + '<div class="wg-set-row"><span>Save code</span><button class="wg-export clickable">📋 Copy save</button></div>'
    + '<textarea class="wg-import-box clickable" placeholder="Paste a save code to restore…"></textarea>'
    + '<button class="wg-import clickable">Load save code</button></div>'
    + '</div>';
  root.appendChild(con);

  type Tab = 'hero' | 'bag' | 'craft' | 'delve' | 'guides' | 'settings';
  const TABS: Tab[] = ['hero', 'bag', 'craft', 'delve', 'guides', 'settings'];
  let activeTab: Tab = 'hero';
  const tabBody = (t: Tab) => con.querySelector('.wg-tab-' + t) as HTMLElement;
  const renderTab: Record<Tab, () => void> = { hero: () => renderHero(), bag: () => renderInv(), craft: () => renderCraft(), delve: () => renderDelve(), guides: () => renderGuides(), settings: () => paintSettings() };
  // "new" badge on the 📖 Guide tab until the player has seen the current build's changelog
  const clBadge = con.querySelector('.wg-cl-badge') as HTMLElement;
  const seenVersion = (() => { try { return localStorage.getItem('wg.lastSeenVersion'); } catch { return BUILD; } })();
  clBadge.classList.toggle('wg-hidden', seenVersion === BUILD);
  const showTab = (t: Tab) => {
    activeTab = t;
    for (const x of TABS) {
      tabBody(x).classList.toggle('wg-hidden', x !== t);
      (con.querySelector(`.wg-con-tab[data-tab="${x}"]`) as HTMLElement).classList.toggle('wg-tab-on', x === t);
    }
    if (t === 'guides') { clBadge.classList.add('wg-hidden'); try { localStorage.setItem('wg.lastSeenVersion', BUILD); } catch { /* private mode */ } }
    renderTab[t]();
  };
  const openConsole = (t?: Tab) => { con.classList.remove('wg-hidden'); root.classList.add('wg-panel-open'); showTab(t ?? activeTab); };
  const closeConsole = () => { con.classList.add('wg-hidden'); root.classList.remove('wg-panel-open'); };
  const consoleOpen = () => !con.classList.contains('wg-hidden');
  const toggleConsole = (t?: Tab) => { (consoleOpen() && (!t || t === activeTab)) ? closeConsole() : openConsole(t); };
  con.querySelectorAll('.wg-con-tab').forEach((b) => b.addEventListener('click', () => showTab((b as HTMLElement).dataset.tab as Tab)));
  (con.querySelector('.wg-con-close') as HTMLElement).onclick = closeConsole;
  menuBtn.onclick = () => toggleConsole();

  // ---- Hero tab: paper-doll (6 gear slots + 2 relics) — click a slot to pick what to wear ----
  const statsEl = con.querySelector('.wg-stats') as HTMLElement;
  const dollFace = con.querySelector('.wg-doll-face') as HTMLCanvasElement;
  const tabFace = con.querySelector('.wg-tabface') as HTMLCanvasElement;
  const slotGrid = con.querySelector('.wg-slotgrid') as HTMLElement;
  const relicRow = con.querySelector('.wg-relicrow') as HTMLElement;
  const passivesEl = con.querySelector('.wg-passives') as HTMLElement;
  const pickerEl = con.querySelector('.wg-picker') as HTMLElement;
  const heroNameEl = con.querySelector('.wg-hero-name') as HTMLElement;
  const SLOT_EMPTY = '#8a8478';
  const AFFIX_ICON: Record<AffixId, string> = { atk: '⚔', hp: '❤️', def: '🛡', spd: '👟', crit: '💥' };
  type PickTarget = { kind: 'gear'; slot: Slot } | { kind: 'relic'; index: number } | null;
  let picking: PickTarget = null;
  const samePick = (a: PickTarget, b: PickTarget) =>
    !!a && !!b && a.kind === b.kind && (a.kind === 'gear' ? a.slot === (b as { slot: Slot }).slot : a.index === (b as { index: number }).index);

  // Draw the player's own character (the baked sprite geometry, in 2D) — "this is me".
  const swatch = (arr: number[], i: number): string => '#' + arr[((i % arr.length) + arr.length) % arr.length].toString(16).padStart(6, '0');
  function drawFigure(cvs: HTMLCanvasElement, pal: { body: number; hair: number; outfit: number }, worn?: { cloak?: string; weapon?: boolean }): void {
    const ctx = cvs.getContext('2d'); if (!ctx) return;
    const W = cvs.width, H = cvs.height;
    ctx.clearRect(0, 0, W, H);
    const s = Math.min(W / 30, H / 30);          // sprite art centered near (17,17) in a 44×40 frame
    const ox = W / 2 - 17 * s, oy = H / 2 - 16 * s;
    const px = (x: number, y: number) => [x * s + ox, y * s + oy] as const;
    const skin = swatch(PALETTE.body, pal.body), hair = swatch(PALETTE.hair, pal.hair), outfit = swatch(PALETTE.outfit, pal.outfit);
    ctx.lineJoin = 'round'; ctx.strokeStyle = '#2a1608'; ctx.lineWidth = 2.5 * s;
    if (worn?.cloak) { // a rarity-coloured cloak draped behind, flaring wide past the tunic + a hem below it
      ctx.fillStyle = worn.cloak;
      ctx.beginPath(); ctx.moveTo(...px(9, 13)); ctx.lineTo(...px(25, 13)); ctx.lineTo(...px(32, 38)); ctx.lineTo(...px(2, 38)); ctx.closePath();
      ctx.fill(); ctx.stroke();
    }
    ctx.beginPath(); ctx.roundRect(9 * s + ox, 15 * s + oy, 16 * s, 17 * s, 7 * s); ctx.fillStyle = outfit; ctx.fill(); ctx.stroke(); // tunic
    ctx.beginPath(); ctx.arc(17 * s + ox, 10 * s + oy, 7.5 * s, 0, Math.PI * 2); ctx.fillStyle = skin; ctx.fill(); ctx.stroke(); // head
    ctx.beginPath(); ctx.arc(17 * s + ox, 9.5 * s + oy, 7.9 * s, Math.PI, Math.PI * 2); ctx.closePath(); ctx.fillStyle = hair; ctx.fill(); // hair dome
    if (worn?.weapon) { // a sword held at the right side
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#c9ccd6'; ctx.lineWidth = 2.2 * s; ctx.beginPath(); ctx.moveTo(...px(27, 30)); ctx.lineTo(...px(33, 14)); ctx.stroke(); // blade
      ctx.strokeStyle = '#6a4a24'; ctx.lineWidth = 3 * s; ctx.beginPath(); ctx.moveTo(...px(24, 31)); ctx.lineTo(...px(29, 27)); ctx.stroke(); // hilt
      ctx.lineCap = 'butt';
    }
  }
  function gearSummary(g: GearItem): string {
    const parts: string[] = [];
    const b = empoweredBase(g);
    if (b.atk) parts.push(`⚔${b.atk}`);
    if (b.def) parts.push(`🛡${b.def}`);
    for (const a of g.affixes) parts.push(`+${a.value}${AFFIX_ICON[a.id]}`);
    return parts.join('  ');
  }
  // Total stat contribution of a piece (base + affixes + empower bumps) — for equip-delta previews.
  type Contribution = { atk: number; def: number; hp: number; spd: number; crit: number };
  function contribution(g: GearItem): Contribution {
    const b = empoweredBase(g);
    const c: Contribution = { atk: b.atk ?? 0, def: b.def ?? 0, hp: 0, spd: 0, crit: 0 };
    const add = (id: AffixId, v: number) => {
      if (id === 'atk') c.atk += v; else if (id === 'def') c.def += v; else if (id === 'hp') c.hp += v;
      else if (id === 'spd') c.spd += v; else if (id === 'crit') c.crit += v;
    };
    for (const a of g.affixes) add(a.id, a.value);
    for (const [id, v] of Object.entries(g.empower.bumps)) add(id as AffixId, v ?? 0);
    return c;
  }
  // "+4⚔ -2🛡" — how equipping g would change stats vs whatever is worn in that slot.
  function deltaHtml(g: GearItem, cur?: GearItem): string {
    const a = contribution(g), b = cur ? contribution(cur) : { atk: 0, def: 0, hp: 0, spd: 0, crit: 0 };
    const order: [keyof Contribution, string][] = [['atk', '⚔'], ['def', '🛡'], ['hp', '❤️'], ['crit', '💥'], ['spd', '👟']];
    const parts: string[] = [];
    for (const [k, icon] of order) {
      const d = a[k] - b[k];
      if (d === 0) continue;
      parts.push(`<span style="color:${d > 0 ? '#7bd86a' : '#e07a7a'}">${d > 0 ? '+' : ''}${d}${icon}</span>`);
    }
    return parts.length ? parts.join(' ') : '<span class="wg-pick-even">≈ even</span>';
  }
  function relicSummary(r: RelicItem): string {
    const p = relicPower(r);
    if (p.regenPerS) return `+${p.regenPerS} HP/s`;
    if (p.xpPct) return `+${p.xpPct}% XP`;
    if (p.dodgePct) return `${p.dodgePct}% dodge`;
    if (p.spdPct) return `+${p.spdPct}% speed`;
    if (p.goldPct) return `+${p.goldPct}% gold`;
    return '';
  }
  const openPicker = (t: PickTarget) => { picking = samePick(picking, t) ? null : t; renderHero(); };
  (con.querySelector('.wg-salvage') as HTMLElement).onclick = () => { game.salvageBelow(2); picking = null; renderHero(); };

  function slotCell(icon: string, label: string, item: { name: string; rarity: number; extra?: string } | null, sel: boolean, onClick: () => void): HTMLElement {
    const cell = el('div', 'wg-slot clickable' + (item ? ' wg-slot-on' : '') + (sel ? ' wg-slot-sel' : ''));
    const color = item ? RARITY_HEX[item.rarity] : SLOT_EMPTY;
    cell.innerHTML = `<span class="wg-slot-ic">${icon}</span><span class="wg-slot-body">`
      + `<span class="wg-slot-lbl">${label}</span>`
      + `<span class="wg-slot-nm" style="color:${color}">${item ? item.name + (item.extra ?? '') : '— empty —'}</span></span>`;
    cell.onclick = onClick;
    return cell;
  }

  function renderHero() {
    const snap = game.snapshot();
    heroNameEl.textContent = (snap.player.name || '').trim() || 'Hero';
    const equippedBySlot: Record<string, typeof snap.gear[number]> = {};
    for (const g of snap.gear) if (g.equipped) equippedBySlot[g.slot] = g;
    const cloakG = equippedBySlot.cloak;
    drawFigure(dollFace, snap.player.palette, {
      cloak: cloakG ? RARITY_HEX[cloakG.rarity] : undefined,
      weapon: !!equippedBySlot.weapon,
    });
    drawFigure(tabFace, snap.player.palette); // the tab icon stays clean
    statsEl.innerHTML = `<span>⚔ ${snap.atk}</span><span>🛡 ${snap.def}</span><span>💥 ${snap.crit}%</span><span>❤️ ${snap.player.maxHp}</span>`;

    slotGrid.innerHTML = '';
    for (const slot of SLOTS) {
      const g = equippedBySlot[slot];
      const sel = picking?.kind === 'gear' && picking.slot === slot;
      const cell = slotCell(SLOT_ICON[slot], SLOT_LABEL[slot],
        g ? { name: g.name, rarity: g.rarity, extra: g.empowerLevel > 0 ? ` +${g.empowerLevel}` : '' } : null,
        sel, () => openPicker({ kind: 'gear', slot }));
      cell.dataset.slot = slot;
      slotGrid.appendChild(cell);
    }
    relicRow.innerHTML = '';
    for (let i = 0; i < 2; i++) {
      const uid = snap.equippedRelics[i];
      const r = uid ? snap.relics.find((x) => x.uid === uid) : null;
      const sel = picking?.kind === 'relic' && picking.index === i;
      const cell = slotCell(r ? RELIC_ICON[r.effect] : '🔮', `Relic ${i + 1}`,
        r ? { name: r.name, rarity: r.rarity } : null, sel, () => openPicker({ kind: 'relic', index: i }));
      cell.dataset.relic = String(i);
      cell.classList.add('wg-relic');
      relicRow.appendChild(cell);
    }
    renderPassives(snap);
    renderGrove(snap);
    renderPicker();
  }

  // Grove restoration meter — the long-goal spine: totems lit, progress to the next, boons earned.
  const groveEl = con.querySelector('.wg-grove') as HTMLElement;
  function renderGrove(snap: ReturnType<Game['snapshot']>) {
    const gr = snap.grove;
    const done = gr.stage >= gr.max;
    const pct = done ? 100 : Math.max(0, Math.min(100, Math.round((gr.fed / gr.next) * 100)));
    const boon = gr.stage > 0 ? ` · <span class="wg-grove-boon">+${gr.stage * 5} HP · regen</span>` : '';
    const sub = done
      ? 'The grove shines whole. 🌿'
      : `Feed grove relics at a totem — <b>${gr.next - gr.fed}</b> to the next.`;
    groveEl.innerHTML =
      `<div class="wg-grove-head">🌳 Grove restored — ${gr.stage}/${gr.max}${boon}</div>`
      + `<div class="wg-grove-bar"><div class="wg-grove-fill" style="width:${pct}%"></div></div>`
      + `<div class="wg-grove-sub">${sub}</div>`;
  }

  // "Passives" — makes relics / legendaries / motif bonuses legible so a build reads at a glance.
  function renderPassives(snap: ReturnType<Game['snapshot']>) {
    const rows: { icon: string; text: string }[] = [];
    for (const uid of snap.equippedRelics) {
      if (!uid) continue;
      const r = snap.relics.find((x) => x.uid === uid); if (!r) continue;
      const def = RELIC_EFFECTS.find((e) => e.id === r.effect)!;
      rows.push({ icon: def.icon, text: `${relicSummary(r)} — ${def.desc}` });
    }
    for (const id of snap.legendaries) {
      const l = LEGENDARIES.find((x) => x.id === id); if (l) rows.push({ icon: '⭐', text: l.desc });
    }
    if (snap.motif.lifestealPct > 0) rows.push({ icon: '🐺', text: `${MOTIF_NAMES.wolf} set: +${snap.motif.lifestealPct}% lifesteal` });
    if (snap.motif.emberPct > 0) rows.push({ icon: '🔥', text: `${MOTIF_NAMES.ember} set: +${snap.motif.emberPct}% gold` });
    if (snap.motif.hpFlat > 0) rows.push({ icon: '🌿', text: `${MOTIF_NAMES.root} set: +${snap.motif.hpFlat} max HP` });
    if (!rows.length) { passivesEl.classList.add('wg-hidden'); passivesEl.innerHTML = ''; return; }
    passivesEl.classList.remove('wg-hidden');
    passivesEl.innerHTML = '<div class="wg-passives-head">Passives</div>';
    for (const r of rows) {
      const row = el('div', 'wg-passive-row');
      row.innerHTML = `<span class="wg-passive-ic">${r.icon}</span><span>${r.text}</span>`;
      passivesEl.appendChild(row);
    }
  }

  function pickRow(html: string, cls: string, onClick: () => void): HTMLElement {
    const row = el('div', 'wg-pick-row clickable' + cls, html);
    row.onclick = onClick;
    return row;
  }
  function renderPicker() {
    if (!picking) { pickerEl.classList.add('wg-hidden'); pickerEl.innerHTML = ''; return; }
    pickerEl.classList.remove('wg-hidden');
    pickerEl.innerHTML = '';
    if (picking.kind === 'gear') {
      const slot = picking.slot;
      pickerEl.appendChild(el('div', 'wg-picker-head', `${SLOT_ICON[slot]} Choose your ${SLOT_LABEL[slot]}`));
      const equippedUid = game.save.equipped[slot];
      if (equippedUid) pickerEl.appendChild(pickRow('✕ Unequip', ' wg-pick-off', () => { game.unequip(slot); renderHero(); }));
      const curG = equippedUid ? game.save.gear.find((x) => x.uid === equippedUid) : undefined;
      const items = [...game.save.gear, ...game.save.satchel.gear].filter((g) => g.slot === slot).sort((a, b) => b.rarity - a.rarity);
      if (!items.length) pickerEl.appendChild(el('div', 'wg-empty', 'Nothing for this slot yet — craft or loot one.'));
      for (const g of items) {
        const on = g.uid === equippedUid;
        const motif = g.motif ? ' ' + MOTIF_ICON[g.motif] : '';
        // equipped row shows its absolute stats; the rest show the delta if you swap them in
        const detail = on ? `<span class="wg-pick-sum">${gearSummary(g)}</span>` : `<span class="wg-pick-sum">${deltaHtml(g, curG)}</span>`;
        const html = `<span class="wg-g-dot" style="background:${RARITY_HEX[g.rarity]}"></span>`
          + `<span class="wg-pick-nm" style="color:${RARITY_HEX[g.rarity]}">${g.name}${g.empower.level > 0 ? ` +${g.empower.level}` : ''}${g.legendary ? ' ⭐' : ''}${g.crafted ? ' 🔨' : ''}${motif}</span>`
          + detail
          + (on ? '<span class="wg-pick-eqbadge">✓</span>' : '<button class="wg-emp clickable" title="Empower with gold">⚒</button>');
        const row = pickRow(html, on ? ' wg-eq' : '', () => { if (!on) { game.equip(g.uid); picking = null; } renderHero(); });
        const emp = row.querySelector('.wg-emp');
        if (emp) (emp as HTMLElement).onclick = (e) => { e.stopPropagation(); game.empower(g.uid); renderHero(); };
        pickerEl.appendChild(row);
      }
    } else {
      const idx = picking.index;
      pickerEl.appendChild(el('div', 'wg-picker-head', `🔮 Choose Relic ${idx + 1}`));
      const equippedUid = game.save.equippedRelics[idx];
      if (equippedUid) pickerEl.appendChild(pickRow('✕ Unequip', ' wg-pick-off', () => { game.unequipRelic(idx); renderHero(); }));
      const relics = [...game.save.relics].sort((a, b) => b.rarity - a.rarity);
      if (!relics.length) pickerEl.appendChild(el('div', 'wg-empty', 'Relics drop from dungeon bosses & chests.'));
      const otherIdx = idx === 0 ? 1 : 0;
      for (const r of relics) {
        const on = r.uid === equippedUid;
        const worn = on || game.save.equippedRelics[otherIdx] === r.uid;
        const wornOther = !on && worn;
        const html = `<span class="wg-slot-ic">${RELIC_ICON[r.effect]}</span>`
          + `<span class="wg-pick-nm" style="color:${RARITY_HEX[r.rarity]}">${r.name}${wornOther ? ` (slot ${otherIdx + 1})` : ''}</span>`
          + `<span class="wg-pick-sum">${relicSummary(r)}</span>`
          + (on ? '<span class="wg-pick-eqbadge">✓</span>' : worn ? '' : '<button class="wg-emp wg-relic-salv clickable" title="Salvage for gold">♻️</button>');
        const row = pickRow(html, on ? ' wg-eq' : '', () => { game.equipRelic(r.uid, idx); picking = null; renderHero(); });
        const salv = row.querySelector('.wg-relic-salv');
        if (salv) (salv as HTMLElement).onclick = (e) => { e.stopPropagation(); game.salvageRelic(r.uid); renderHero(); };
        pickerEl.appendChild(row);
      }
    }
  }

  // ---- Bag tab (inventory grid) ----
  const invGrid = con.querySelector('.wg-inv-grid') as HTMLElement;
  function renderInv() {
    invGrid.innerHTML = '';
    const inv = game.save.inventory;
    const keys = Object.keys(inv);
    if (keys.length === 0) invGrid.innerHTML = '<div class="wg-empty">Empty — go gather!</div>';
    for (const id of keys) {
      const def = ITEMS[id];
      const act = def?.kind === 'food' ? 'eat' : def?.kind === 'placeable' ? 'place' : '';
      const cell = el('div', 'wg-cell' + (act ? ' wg-eatable clickable' : ''));
      cell.innerHTML = `<span class="wg-ic">${def?.icon ?? '❔'}</span><span class="wg-nm">${def?.name ?? id}</span><span class="wg-ct">${inv[id]}</span>${act ? `<span class="wg-act-lbl">${act}</span>` : ''}`;
      if (act === 'eat') cell.onclick = () => { game.eat(id); renderInv(); };
      else if (act === 'place') cell.onclick = () => { game.buildFacing(id); renderInv(); };
      invGrid.appendChild(cell);
    }
  }

  // ---- Craft tab (by category) ----
  const PLACEABLES = ['campfire', 'torch', 'wooden_wall', 'home_portal'];
  const CATS = [
    { id: 'tools', name: '🪓 Tools' }, { id: 'weapons', name: '⚔️ Weapons' }, { id: 'armor', name: '🛡️ Armor' },
    { id: 'build', name: '🏗️ Build' }, { id: 'refine', name: '🍖 Cook & Refine' },
  ] as const;
  const catOf = (r: typeof RECIPES[number]): string => {
    if (r.out.kind === 'tool') return 'tools';
    if (r.out.kind === 'weapon') return 'weapons';
    if (r.out.kind === 'armor') return 'armor';
    return r.out.kind === 'item' && PLACEABLES.includes(r.out.item) ? 'build' : 'refine';
  };
  const nearEl = con.querySelector('.wg-near') as HTMLElement;
  const catChips = con.querySelector('.wg-cat-chips') as HTMLElement;
  const craftList = con.querySelector('.wg-craft-list') as HTMLElement;
  let activeCat = 'tools';
  function renderCraft() {
    const inv = game.save.inventory;
    const near = game.nearCampfire();
    nearEl.innerHTML = near ? '🔥 Near a campfire' : '<span class="wg-dim">🔥 Some recipes need a campfire nearby</span>';
    catChips.innerHTML = '';
    for (const c of CATS) {
      const chip = el('button', 'wg-chip clickable' + (activeCat === c.id ? ' wg-chip-on' : ''), c.name);
      chip.onclick = () => { activeCat = c.id; renderCraft(); };
      catChips.appendChild(chip);
    }
    craftList.innerHTML = '';
    const inCat = RECIPES.filter((r) => catOf(r) === activeCat);
    // craftable-now first
    inCat.sort((a, b) => Number(canCraft(b, inv, near)) - Number(canCraft(a, inv, near)));
    if (!inCat.length) craftList.innerHTML = '<div class="wg-empty">Nothing here yet</div>';
    for (const r of inCat) {
      const ok = canCraft(r, inv, near);
      const row = el('div', 'wg-recipe' + (ok ? ' wg-can clickable' : ''));
      const needs = Object.entries(r.needs).map(([it, n]) => {
        const have = inv[it] ?? 0;
        return `<span class="${have >= (n || 0) ? 'wg-ok' : 'wg-miss'}">${ITEMS[it]?.icon ?? ''}${have}/${n}</span>`;
      }).join(' ');
      row.innerHTML = `<span class="wg-r-ic">${r.icon}</span><span class="wg-r-nm">${r.name}${r.near ? ' 🔥' : ''}</span><span class="wg-r-need">${needs}</span>`;
      if (ok) row.onclick = () => { game.craft(r.id); renderCraft(); };
      craftList.appendChild(row);
    }
  }

  // ---- Delve tab (dungeon: tier · deepest · best times · blessing) ----
  const delveEl = tabBody('delve');
  function renderDelve() {
    const s = game.snapshot();
    const bests = Object.entries(s.bests).sort((a, b) => Number(a[0]) - Number(b[0]));
    delveEl.innerHTML =
      `<div class="wg-delve-hero">⛓️ Deepest floor reached: <b>${s.deepest}</b></div>`
      + '<div class="wg-delve-sec">Difficulty tier <span class="wg-dim">(higher = tougher mobs, better loot)</span></div>'
      + '<div class="wg-tier"><span class="wg-tier-btns"></span></div>'
      + '<div class="wg-delve-sec">Loot rarity</div>'
      + '<div class="wg-rarity-legend">'
      + RARITY_NAMES.map((n, i) => `<span class="wg-rar-chip"><span class="wg-g-dot" style="background:${RARITY_HEX[i]}"></span><span style="color:${RARITY_HEX[i]}">${n}</span></span>`).join('')
      + '</div>'
      + '<div class="wg-dim wg-rar-note">Deeper floors &amp; higher tiers drop rarer gear. Glowing overworld elites drop Common–Uncommon; dungeon bosses guarantee Rare+ with a shot at Epic &amp; Legendary (⭐ = a unique power).</div>'
      + '<div class="wg-delve-sec">Best clear times</div>'
      + (bests.length ? '<div class="wg-bests">' + bests.map(([f, t]) => `<div class="wg-best-row"><span>Floor ${f}</span><span>${t}s</span></div>`).join('') + '</div>'
        : '<div class="wg-empty">No floors cleared yet</div>')
      + '<div class="wg-delve-sec">Grove blessing</div>'
      + `<div class="wg-bless-info">Today: <b>${s.blessing.today}</b> · Banked: ${s.blessing.banked}${s.blessing.active ? ` · Active: ${s.blessing.active}` : ''}</div>`
      + (s.blessing.claimable ? '<button class="wg-claim-bless clickable">🌸 Claim today’s blessing</button>' : '');
    const tb = delveEl.querySelector('.wg-tier-btns') as HTMLElement;
    for (let t = 1; t <= 3; t++) {
      const b = el('button', 'wg-tier-b clickable' + (s.tier === t ? ' wg-tier-on' : ''), String(t));
      b.onclick = () => { game.setTier(t); renderDelve(); };
      tb.appendChild(b);
    }
    const cb = delveEl.querySelector('.wg-claim-bless') as HTMLElement | null;
    if (cb) cb.onclick = () => { game.claimBlessing(); renderDelve(); };
  }

  // ---- Guides tab (the full how-to lives here; the intro popup stays light) ----
  const guidesEl = con.querySelector('.wg-tab-guides') as HTMLElement;
  let guidesBuilt = false;
  const sec = (title: string, body: string, open = false) => `<details class="wg-gd"${open ? ' open' : ''}><summary>${title}</summary><div class="wg-gd-body">${body}</div></details>`;
  // creature icons for the bestiary (baked sprites don't render in the DOM, so a flavour emoji each)
  const MOB_EMOJI: Record<string, string> = { slime: '🟢', wolf: '🐺', skeleton: '💀', boar: '🐗', direwolf: '🐕‍🦺', wraith: '👻', wisp: '✨', golden_slime: '🟡', bone_warden: '☠️' };
  const codexCell = (icon: string, name: string, seen: boolean) =>
    `<div class="wg-cx-cell${seen ? '' : ' wg-cx-locked'}"><span class="wg-cx-ico">${seen ? icon : '❔'}</span><span class="wg-cx-name">${seen ? name : '???'}</span></div>`;
  // The living collection log — re-rendered on every Guide open so it reflects fresh finds.
  function renderCodex() {
    const cx = guidesEl.querySelector('.wg-codex') as HTMLElement | null;
    if (!cx) return;
    const cd = game.snapshot().codex;
    const mobIds = Object.keys(MOBS);
    const grid = (cells: string) => `<div class="wg-cx-grid">${cells}</div>`;
    const bestiary = mobIds.map((id) => codexCell(MOB_EMOJI[id] ?? '❔', MOBS[id].name, cd.mobs.includes(id))).join('');
    const relics = RELIC_EFFECTS.map((e) => codexCell(e.icon, e.name, cd.relics.includes(e.id))).join('');
    const recipes = RECIPES.map((r) => codexCell(r.icon, r.name, cd.recipes.includes(r.id))).join('');
    const seen = (list: string[], all: string[]) => list.filter((x) => all.includes(x)).length;
    cx.innerHTML =
      sec(`🐾 Bestiary (${seen(cd.mobs, mobIds)}/${mobIds.length})`, grid(bestiary), true)
      + sec(`💎 Relics (${seen(cd.relics, RELIC_EFFECTS.map((e) => e.id))}/${RELIC_EFFECTS.length})`, grid(relics))
      + sec(`🔨 Recipes (${seen(cd.recipes, RECIPES.map((r) => r.id))}/${RECIPES.length})`, grid(recipes));
  }
  function renderGuides() {
    if (guidesBuilt) { renderCodex(); return; } // static built; just refresh the live collection
    guidesBuilt = true;
    const rarityRow = RARITY_NAMES.map((n, i) => `<span class="wg-rar-chip"><span class="wg-g-dot" style="background:${RARITY_HEX[i]}"></span><span style="color:${RARITY_HEX[i]}">${n}</span></span>`).join('');
    const whatsNew = CHANGELOG.map((c) => `<div class="wg-cl-entry"><div class="wg-cl-ver">${c.v} <span class="wg-cl-date">${c.date}</span></div><ul>${c.notes.map((n) => `<li>${n}</li>`).join('')}</ul></div>`).join('');
    guidesEl.innerHTML =
      sec('🆕 What’s new', whatsNew, true)
      + '<details class="wg-gd" open><summary>📖 Collection</summary><div class="wg-gd-body wg-codex"></div></details>'
      + '<div class="wg-guides-intro">Everything you need — dip in anytime. Follow the <b>Goal</b> at the top of the screen and you\'ll never be lost.</div>'
      + sec('🕹️ Controls', '<ul>'
        + '<li><b>Move</b> — WASD / arrow keys, or the joystick (bottom-left on touch).</li>'
        + '<li><b>Gather / interact</b> — <b>E</b> or the ✋ button: trees, rocks, bushes, chests, dungeon doors.</li>'
        + '<li><b>Attack</b> — <b>Space</b> / click, or the ⚔️ button.</li>'
        + '<li><b>Switch weapon</b> — <b>1</b> melee · <b>2</b> bow.</li>'
        + '<li><b>Panels</b> — the 🎒 button, or <b>P</b> Hero · <b>I</b> Bag · <b>C</b> Craft · <b>B</b> Dungeon · <b>G</b> Guide.</li></ul>', true)
      + sec('🌿 Gather &amp; Craft', '<ul>'
        + '<li>Collect <b>wood, fiber, stone, berries</b> from the world. Tougher nodes (iron, gold) need better <b>pickaxes</b>.</li>'
        + '<li>Build things in the <b>🔨 Craft</b> tab — tools, weapons, armor, campfires, walls.</li>'
        + '<li>A <b>campfire</b> unlocks smelting (iron bars) and cooking — stand near one to make those.</li></ul>')
      + sec('🔥 Survive', '<ul>'
        + '<li><b>Food is a buff, never hunger</b> — eat from your 🎒 Bag for more max HP, faster regen and speed. You never starve.</li>'
        + '<li><b>Day &amp; night</b> — nights are dangerous and skeletons roam. Watch the sun/moon clock (top-right).</li>'
        + '<li>Your <b>home Sanctuary</b> is a safe ring: nothing spawns or hurts you inside. Expand it in ⚙️ Settings.</li></ul>')
      + sec('⚔️ Fight', '<ul>'
        + '<li>Wolves, slimes and boars are <b>peaceful until you strike them</b>. Skeletons on <i>corrupted ground</i> and night prowlers attack on sight.</li>'
        + '<li>Kill for hide, meat and gold. <b>Creatures with an amber glow are elites</b> — they can drop gear out in the world.</li></ul>')
      + sec('⛓️ Dungeons', '<ul>'
        + '<li>Find a dark <b>Dungeon door</b> in the world. Pick a <b>difficulty tier</b> (1–3) in the ⛓️ Dungeon tab — higher = tougher, better loot. Replay any tier freely.</li>'
        + '<li>A dungeon is <b>10 floors</b>. Bosses guard floors <b>3</b> &amp; <b>8</b> (mini), <b>5</b> (Bone Warden), and <b>10</b> — <b>The Hollow King</b>. Beat floor 10 to <b>clear the dungeon</b>.</li>'
        + '<li>Loot rides your <b>satchel</b> and is <i>at risk</i> until you leave — bank it with <b>⬆ Leave</b>. The floor-5 Bone Warden guarantees <b>Epic+ gear</b>; the floor-10 <b>Hollow King</b> drops a <b>grove relic</b>.</li></ul>')
      + sec('🌳 Restore the Grove', '<ul>'
        + '<li>The grove is <b>dimming</b> — colour has drained from the world. Carry <b>grove relics</b> home from the Hollow King and press <b>E</b> at a <b>totem</b> to pour them in.</li>'
        + '<li>Each totem you light <b>returns colour</b> and grants a small <b>permanent boon</b> (more max HP + regen). Later totems cost more — but your growing strength earns relics faster.</li></ul>')
      + sec('💎 Loot &amp; Gear', '<ul>'
        + `<li>Rarity: <span class="wg-rarity-legend" style="display:inline-flex;gap:8px">${rarityRow}</span> — deeper &amp; harder means rarer.</li>`
        + '<li>Wear gear in the <b>👤 Hero</b> panel: 6 slots (weapon, sub-weapon, armor, necklace, ring, cloak) + 2 relics. Tap a slot to choose what to wear — green ↑ / red ↓ shows the stat change.</li>'
        + '<li><b>⚒ Empower</b> spends gold to upgrade a piece; <b>♻️ Salvage</b> turns spares into gold.</li></ul>')
      + sec('💾 Your progress', '<ul>'
        + '<li>Saved automatically <b>in this browser</b>. <b>Death just sends you home</b> — you keep everything and drop only a little gold. Never game-over.</li>'
        + '<li>On phones, <b>Add to Home Screen</b> to keep your world safe; or copy a <b>save code</b> in ⚙️ Settings as backup.</li></ul>');
    renderCodex(); // fill the collection placeholder now that the static shell exists
  }

  // ---- Settings tab ----
  const soundBtn = con.querySelector('.wg-sound') as HTMLButtonElement;
  const setSetMsg = (btn: HTMLElement, ok: string) => { const t = btn.textContent; btn.textContent = ok; setTimeout(() => { btn.textContent = t; }, 1400); };
  const paintSound = () => { soundBtn.textContent = sfx && !sfx.muted ? '🔊 On' : '🔇 Off'; };
  soundBtn.onclick = () => { if (sfx) { sfx.setMuted(!sfx.muted); paintSound(); } };
  const sanctBtn = con.querySelector('.wg-sanct') as HTMLButtonElement;
  const sanctLbl = con.querySelector('.wg-sanct-lbl') as HTMLElement;
  const paintSanct = () => {
    const s = game.snapshot();
    sanctLbl.textContent = `Sanctuary · Lv ${s.sanctuary}/${s.sanctuaryMax}`;
    if (s.sanctuaryCost) { sanctBtn.textContent = `Expand · 🪙${s.sanctuaryCost.gold} + 🪨${s.sanctuaryCost.stone}`; sanctBtn.disabled = false; }
    else { sanctBtn.textContent = 'Max width'; sanctBtn.disabled = true; }
  };
  // --- optional Account section (cloud save-sync); only appears when the cloud layer is configured ---
  const accountEl = con.querySelector('.wg-account') as HTMLElement;
  const STATUS_LINE: Record<string, string> = {
    'signed-out': 'Not signed in', 'signing-in': 'Signing in…', syncing: 'Syncing…', synced: 'Synced ✓',
  };
  function renderAccount() {
    if (!account) { accountEl.classList.add('wg-hidden'); return; }
    accountEl.classList.remove('wg-hidden');
    const st = account.status();
    const line = st === 'error' ? `⚠ ${account.message()}` : (STATUS_LINE[st] ?? '');
    const email = account.email();
    if (email) {
      accountEl.innerHTML = '<div class="wg-acc-head">☁ Account</div>'
        + `<div class="wg-acc-who">Signed in as <b>${email}</b></div>`
        + `<div class="wg-acc-status">${line}</div>`
        + '<div class="wg-acc-btns"><button class="wg-acc-sync clickable">Sync now</button><button class="wg-acc-out clickable">Sign out</button></div>';
      (accountEl.querySelector('.wg-acc-sync') as HTMLElement).onclick = () => account!.push();
      (accountEl.querySelector('.wg-acc-out') as HTMLElement).onclick = () => { void account!.signOut(); };
    } else {
      accountEl.innerHTML = '<div class="wg-acc-head">☁ Account <span class="wg-acc-opt">— optional cloud save</span></div>'
        + '<input class="wg-acc-email clickable" type="email" placeholder="email" autocomplete="email">'
        + '<input class="wg-acc-pw clickable" type="password" placeholder="password" autocomplete="current-password">'
        + '<div class="wg-acc-btns"><button class="wg-acc-in clickable">Sign in</button><button class="wg-acc-up clickable">Create account</button></div>'
        + (line ? `<div class="wg-acc-status">${line}</div>` : '');
      const email2 = () => (accountEl.querySelector('.wg-acc-email') as HTMLInputElement).value.trim();
      const pw = () => (accountEl.querySelector('.wg-acc-pw') as HTMLInputElement).value;
      (accountEl.querySelector('.wg-acc-in') as HTMLElement).onclick = () => { void account!.signIn(email2(), pw()).catch(() => { /* status shows the error */ }); };
      (accountEl.querySelector('.wg-acc-up') as HTMLElement).onclick = () => { void account!.signUp(email2(), pw()).catch(() => { /* status shows the error */ }); };
    }
  }
  account?.onChange(() => { if (activeTab === 'settings') renderAccount(); });

  const paintSettings = () => { paintSound(); paintSanct(); renderAccount(); };
  sanctBtn.onclick = () => { game.expandSanctuary(); paintSanct(); };
  (con.querySelector('.wg-share') as HTMLElement).onclick = (e) => {
    const link = `${location.origin}${location.pathname}?seed=${game.snapshot().seed}`;
    navigator.clipboard?.writeText(link).catch(() => { /* clipboard blocked */ });
    setSetMsg(e.currentTarget as HTMLElement, '✓ Copied!');
  };
  // feedback: open the issue tracker (or the owner's form) with the build tag pre-filled in the title
  (con.querySelector('.wg-feedback') as HTMLElement).onclick = () => {
    window.open(`${FEEDBACK_URL}?title=${encodeURIComponent('[' + BUILD + '] ')}`, '_blank', 'noopener');
  };
  (con.querySelector('.wg-export') as HTMLElement).onclick = (e) => {
    navigator.clipboard?.writeText(game.exportSave()).catch(() => { /* clipboard blocked */ });
    setSetMsg(e.currentTarget as HTMLElement, '✓ Copied!');
  };
  const importBox = con.querySelector('.wg-import-box') as HTMLTextAreaElement;
  (con.querySelector('.wg-import') as HTMLElement).onclick = () => {
    const code = importBox.value.trim();
    if (!code) return;
    if (game.importSave(code)) location.reload();
    else { importBox.value = ''; importBox.placeholder = 'That code was not valid — try again.'; }
  };

  // --- touch controls (shown on coarse pointers) ---
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  // MUST be .clickable — #ui-root is pointer-events:none, so without it the joystick is
  // transparent to touches and they fall through to the canvas (fires attack, never moves).
  const joy = el('div', 'wg-joy clickable' + (coarse ? '' : ' wg-hidden'));
  const joyNub = el('div', 'wg-joy-nub');
  joy.appendChild(joyNub);
  root.appendChild(joy);
  const actBtn = el('button', 'wg-btn wg-act clickable' + (coarse ? '' : ' wg-hidden'), '✋');
  const atkBtn = el('button', 'wg-btn wg-atk clickable' + (coarse ? '' : ' wg-hidden'), '⚔️');
  root.append(actBtn, atkBtn);
  setupJoystick(joy, joyNub, game);
  actBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); game.tryInteract(); });
  atkBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); game.tryAttack(); });

  // --- welcome popup (light; auto-shows only on the very first session — the full manual is the 📖 Guide tab) ---
  const guideSeen = (() => { try { return localStorage.getItem('wg.seenGuide') === '1'; } catch { return false; } })();
  buildGuide(root, coarse, !guideSeen);

  // --- install hint (keeps saves safe on iOS; one-tap install on Android) ---
  setupInstallHint(root, game);

  // --- dungeon HUD (floor + satchel + leave) ---
  const dun = el('div', 'wg-dun wg-hidden');
  dun.innerHTML = '<div class="wg-dun-floor"></div><div class="wg-dun-satchel"></div><button class="wg-dun-leave clickable">⬆ Leave Dungeon</button>';
  root.appendChild(dun);
  (dun.querySelector('.wg-dun-leave') as HTMLElement).onclick = () => game.exitDungeon();
  const dunFloor = dun.querySelector('.wg-dun-floor') as HTMLElement;
  const dunSatchel = dun.querySelector('.wg-dun-satchel') as HTMLElement;

  // --- blessing picker (pick 1 of 3 on each descent) ---
  const bless = el('div', 'wg-bless wg-hidden clickable');
  bless.innerHTML = '<h3>Choose a floor blessing</h3><p class="wg-bless-sub"></p><div class="wg-bless-row"></div>';
  const blessSub = bless.querySelector('.wg-bless-sub') as HTMLElement;
  const blessRow = bless.querySelector('.wg-bless-row') as HTMLElement;
  BLESSINGS.forEach((b, i) => {
    const c = el('button', 'wg-bless-card clickable', b.label);
    c.onclick = () => game.pickBlessing(i);
    blessRow.appendChild(c);
  });
  root.appendChild(bless);

  // --- grove blessing claim now lives in the console's Delve tab ---

  // --- dev overlay (perf) — dev hosts only; players never see fps/chunk counts ---
  const dev = el('div', 'wg-dev');
  const isDevHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(location.hostname);
  if (!isDevHost) dev.style.display = 'none';
  root.appendChild(dev);

  // --- toast handler ---
  const showToast = (html: string, holdMs = 1400) => {
    const t = el('div', 'wg-toast', html);
    toasts.appendChild(t);
    setTimeout(() => t.classList.add('wg-toast-out'), holdMs);
    setTimeout(() => t.remove(), holdMs + 500);
  };
  game.on('toast', (p) => {
    const { icon, text } = p as { icon?: string; text: string };
    showToast(`${icon ?? ''} ${text}`);
  });

  // gentle once-per-load nudge: a banked daily blessing is waiting (the return trigger)
  if (game.snapshot().blessing.claimable) {
    setTimeout(() => showToast('🌸 A grove blessing is ready — claim it in the ⛓️ Dungeon tab', 3400), 900);
  }

  // --- narrative: a light line surfaced once when a milestone is first reached ---
  const NARRATIVE: Record<string, string> = {
    firstWood: 'The grove gives freely — while its light still holds.',
    campfire: 'A fire catches. Warmth pushes the dark back an arm’s length.',
    stoneTools: 'Knapped stone bites deeper than bare hands ever could.',
    firstNightfall: 'Night settles over the grove. Below, something wakes to the scent of light.',
    firstDungeon: 'The door breathes cold air up from the deep. Down is the only way on.',
    firstBoss: 'A Warden falls, and the grove exhales. One ember, returned.',
    firstLegendary: 'This one hums with an old, patient power. It has chosen you.',
  };
  const narr = el('div', 'wg-narr wg-hidden');
  root.appendChild(narr);

  // --- juice: a screen-edge flash for the big moments (rare+ drops, level-ups) ---
  const flash = el('div', 'wg-flash');
  root.appendChild(flash);
  const screenFlash = (rgb: string, peak: number) => {
    flash.style.background = `radial-gradient(ellipse at center, transparent 42%, ${rgb} 100%)`;
    flash.style.transition = 'none';
    flash.style.opacity = String(peak);
    requestAnimationFrame(() => { flash.style.transition = 'opacity 650ms ease-out'; flash.style.opacity = '0'; });
  };
  // a found item worth celebrating: legendary → gold, epic → violet, rare → a subtle blue
  game.on('loot', (p) => {
    const d = p as { rarity?: number; legendary?: boolean };
    if (d.legendary) screenFlash('rgba(240,160,48,0.60)', 0.6);
    else if ((d.rarity ?? 0) >= 3) screenFlash('rgba(176,96,224,0.52)', 0.52);
    else if ((d.rarity ?? 0) >= 2) screenFlash('rgba(74,144,226,0.32)', 0.32);
  });
  game.on('levelup', () => screenFlash('rgba(240,205,110,0.34)', 0.34)); // a warm progression pulse

  let narrTimer = 0;
  const showNarr = (line: string, holdMs = 4200) => {
    narr.textContent = line;
    narr.classList.remove('wg-hidden', 'wg-narr-out');
    clearTimeout(narrTimer);
    narrTimer = window.setTimeout(() => {
      narr.classList.add('wg-narr-out');
      window.setTimeout(() => narr.classList.add('wg-hidden'), 800);
    }, holdMs);
  };
  game.on('milestone', (p) => {
    const line = NARRATIVE[(p as { id: string }).id];
    if (line) showNarr(line);
  });
  // grove restoration: a totem lit → narrative + a pulse as colour returns; the final totem
  // (the grove made whole) gets a bigger, golden celebration.
  game.on('grove-stage', (p) => {
    const g = p as { line: string; stage: number; max: number };
    const whole = g.stage >= g.max;
    showNarr(g.line, whole ? 6500 : 5200);
    screenFlash(whole ? 'rgba(240,205,110,0.52)' : 'rgba(120,230,150,0.30)', whole ? 0.52 : 0.3);
  });

  // --- death dialog (respawn confirmation) ---
  const death = el('div', 'wg-death wg-hidden');
  death.innerHTML = '<div class="wg-death-card"><h2>💀 You fell</h2><p class="wg-death-msg"></p><button class="wg-death-ok clickable">Wake at Home</button></div>';
  root.appendChild(death);
  const deathMsg = death.querySelector('.wg-death-msg') as HTMLElement;
  (death.querySelector('.wg-death-ok') as HTMLElement).onclick = () => death.classList.add('wg-hidden');
  game.on('player-died', (p) => {
    const d = p as { place?: string; lostGold?: number; floor?: number };
    deathMsg.innerHTML = d.place === 'dungeon'
      ? `The depths of <b>Dungeon · Floor ${d.floor ?? '?'}</b> took you. Only a sliver of your satchel (15%) made it out — you wake at your home fire.`
      : `You were struck down in the wild${d.lostGold ? ` and dropped ${d.lostGold} gold` : ''}. You wake at your home fire, restored.`;
    // reassure the first time — death here isn't a game-over, and your world persists
    const firstDeath = (() => { try { if (localStorage.getItem('wg.diedOnce')) return false; localStorage.setItem('wg.diedOnce', '1'); return true; } catch { return false; } })();
    if (firstDeath) deathMsg.innerHTML += '<span class="wg-death-safe">Your world is safe — you keep your gear, home and everything you’ve built. Death just sends you home. Dive back in.</span>';
    death.classList.remove('wg-hidden');
  });

  // --- portal prompt (step through → travel home?) ---
  const portal = el('div', 'wg-portal wg-hidden');
  portal.innerHTML = '<div class="wg-portal-card"><div class="wg-portal-swirl">🌀</div><h2>The portal yawns open</h2>'
    + '<p>Step through and travel home to your grove?</p>'
    + '<div class="wg-portal-btns"><button class="wg-portal-go clickable">Enter the portal</button>'
    + '<button class="wg-portal-no clickable">Stay here</button></div></div>';
  root.appendChild(portal);
  const closePortal = () => portal.classList.add('wg-hidden');
  (portal.querySelector('.wg-portal-go') as HTMLElement).onclick = () => { game.teleportHome(); closePortal(); };
  (portal.querySelector('.wg-portal-no') as HTMLElement).onclick = () => { game.cancelPortal(); closePortal(); };
  game.on('portal-prompt', () => portal.classList.remove('wg-hidden'));

  // --- dungeon complete (boss down → Exit or keep Exploring) ---
  const done = el('div', 'wg-done wg-hidden');
  done.innerHTML = '<div class="wg-done-card"><div class="wg-done-star">⭐</div><h2 class="wg-done-title">Dungeon cleared!</h2>'
    + '<p class="wg-done-msg"></p><div class="wg-done-btns">'
    + '<button class="wg-done-share clickable">📋 Share run</button>'
    + '<button class="wg-done-explore clickable">Keep exploring</button>'
    + '<button class="wg-done-exit clickable">Leave the dungeon 🔥</button></div></div>';
  root.appendChild(done);
  const doneMsg = done.querySelector('.wg-done-msg') as HTMLElement;
  const closeDone = () => done.classList.add('wg-hidden');
  (done.querySelector('.wg-done-explore') as HTMLElement).onclick = closeDone;
  (done.querySelector('.wg-done-exit') as HTMLElement).onclick = () => { closeDone(); game.exitDungeon(); };
  const doneStar = done.querySelector('.wg-done-star') as HTMLElement;
  const doneTitle = done.querySelector('.wg-done-title') as HTMLElement;
  const doneExplore = done.querySelector('.wg-done-explore') as HTMLElement;
  const doneShare = done.querySelector('.wg-done-share') as HTMLButtonElement;
  // Spoiler-free, copy-paste run card (the Wordle lever) — a floor-10 clear reads very differently
  // from a floor-3 boss-down, so every good run is worth posting.
  const bossEmoji = (f: number): string => (f === 10 ? '👑' : f === 5 ? '⭐' : '💀');
  const runCard = (floor: number, cleared: boolean): string => {
    const s = game.snapshot();
    const bar = '▰'.repeat(floor) + '▱'.repeat(DUNGEON_MAX_FLOOR - floor);
    const bosses = [3, 5, 8, 10].filter((f) => f <= floor).map(bossEmoji).join(' ');
    const head = cleared ? 'Dungeon CLEARED 👑' : `Floor ${floor} reached`;
    const loot = `🎒${s.satchel.gear} · 🪙${s.satchel.gold}`;
    const link = `${location.origin}${location.pathname}`;
    return `🌿 Wildergrove — ${head}\nTier ${s.tier} · ${bar} ${floor}/${DUNGEON_MAX_FLOOR}\nBosses ${bosses}\nLoot ${loot}\nPlay free: ${link}`;
  };
  game.on('dungeon-complete', (p) => {
    const d = p as { floor: number; kind?: string; cleared?: boolean };
    if (d.cleared) { // the final boss — the dungeon is beaten
      doneStar.textContent = '👑'; doneTitle.textContent = 'Dungeon Cleared!';
      doneMsg.innerHTML = `You felled <b>The Hollow King</b> on floor ${d.floor} and claimed the depths — a jackpot of loot and a mighty relic. Leave to bank it all; the cleared dungeon collapses in flame behind you.`;
      doneExplore.textContent = 'Look around';
    } else { // a floor boss — keep going or bail with your loot
      doneStar.textContent = d.kind === 'big' ? '⭐' : '💀';
      doneTitle.textContent = d.kind === 'big' ? 'Warden down!' : 'Boss down!';
      doneMsg.innerHTML = `Floor ${d.floor} boss felled and its spoils are yours. <b>Keep descending</b> for greater rewards, or leave to bank your satchel.`;
      doneExplore.textContent = 'Keep descending';
    }
    doneShare.onclick = () => {
      navigator.clipboard?.writeText(runCard(d.floor, !!d.cleared)).catch(() => { /* clipboard blocked */ });
      const t = doneShare.textContent; doneShare.textContent = '✓ Copied!';
      setTimeout(() => { doneShare.textContent = t; }, 1400);
    };
    done.classList.remove('wg-hidden');
  });

  // --- 1 Hz-ish HUD refresh via rAF ---
  let firstNodeSeen = false;
  function refresh() {
    const s = game.snapshot();
    const maxHp = s.player.maxHp;
    hpFill.style.width = `${Math.max(0, (s.player.hp / maxHp) * 100)}%`;
    hpText.textContent = `${Math.round(s.player.hp)}/${maxHp}`;
    gold.textContent = `🪙 ${s.player.gold}`;
    foodRow.innerHTML = s.foodBuffs > 0
      ? '🍖'.repeat(s.foodBuffs) + '<span class="wg-fed">fed</span>'
      : '<span class="wg-unfed">unfed</span>';
    // reflect the actually-equipped weapon/bow (they're gear now), not a generic label
    const wpn = s.gear.find((g) => g.equipped && g.slot === 'weapon');
    const bow = s.gear.find((g) => g.equipped && g.slot === 'subweapon');
    weaponEl.textContent = s.weapon === 'bow' ? (bow ? `🏹 ${bow.name}` : '🏹 —') : (wpn ? `🗡️ ${wpn.name}` : '✊ Fists');
    safeBadge.classList.toggle('wg-hidden', !s.safe);
    // sun/moon arc: the orb rises at the left horizon, peaks at noon, sets at the right
    const ang = Math.PI * (1 - s.time.phaseFrac); // π (left) → 0 (right)
    orb.style.left = `${20 + Math.cos(ang) * 15 - 8}px`;
    orb.style.top = `${22 - Math.sin(ang) * 15 - 8}px`;
    orb.textContent = s.time.isNight ? '🌙' : '☀️';
    clock.classList.toggle('wg-is-night', s.time.isNight);
    clockLbl.textContent = s.time.label;
    night.style.opacity = String(s.time.darkness);
    // compass: overworld → points home; in a dungeon → points to the objective (boss, then stairs)
    const nav = s.dungeonNav;
    if (nav) {
      compArrow.style.transform = `rotate(${(nav.angleRad * 180) / Math.PI + 90}deg)`;
      compArrow.style.color = nav.target === 'boss' ? '#e87070' : '#8ac8f0';
      compLbl.textContent = nav.target === 'boss' ? `⚠ ${nav.distTiles}m` : `⬇ ${nav.distTiles}m`;
    } else {
      compArrow.style.transform = `rotate(${(s.home.angleRad * 180) / Math.PI + 90}deg)`;
      compArrow.style.color = '#e8c060';
      compLbl.textContent = s.home.distTiles === 0 ? 'home' : `${s.home.distTiles}m`;
    }
    fog.classList.toggle('wg-hidden', s.mode !== 'dungeon');
    // dungeon HUD + blessing picker
    if (s.mode === 'dungeon') {
      dun.classList.remove('wg-hidden');
      const bk = bossKindForFloor(s.floor);
      dunFloor.textContent = `⛓️ Dungeon · Floor ${s.floor}${s.bossAlive && bk ? ` · ⚠ ${bossName(bk)}` : ''}`;
      const loot = Object.entries(s.satchel.items).map(([it, n]) => `${ITEMS[it]?.icon ?? ''}${n}`).join(' ');
      dunSatchel.textContent = `🎒 🪙${s.satchel.gold}${loot ? ' ' + loot : ''}`;
    } else dun.classList.add('wg-hidden');
    if (s.pendingBlessing) {
      // floor-aware flavour: the bottom floor has no "down" — the Dungeon Boss is the way on
      blessSub.textContent = s.floor >= DUNGEON_MAX_FLOOR
        ? 'The Hollow King waits ahead — take an edge into the final fight.'
        : 'Steel yourself for the descent — take one blessing.';
      narr.classList.add('wg-hidden'); // don't stack a narrative line on top of the blessing prompt
    }
    bless.classList.toggle('wg-hidden', !s.pendingBlessing);
    hint.classList.toggle('wg-hidden', s.pendingBlessing); // the prompt sits below the hero — clear the hint under it
    goal.classList.toggle('wg-hidden', s.mode === 'dungeon');
    goal.textContent = goalFor(game);
    // contextual bottom hint
    if (s.mode === 'dungeon') {
      hint.textContent = coarse ? '✋ open chest / descend · ⚔️ attack' : 'E open chest · descend at stairs · Space attack';
    } else {
      hint.textContent = coarse
        ? '✋ gather · ⚔️ attack' + (s.mobs > 0 ? "  — something's nearby!" : '')
        : 'E gather/enter · Space attack · 1/2 fists/bow' + (s.mobs > 0 ? "  — something's nearby!" : '');
    }
    // nudge the menu button when a blessing is claimable (it's in the Delve tab)
    menuBtn.classList.toggle('wg-menu-alert', s.blessing.claimable && !consoleOpen());
    const perf = game.perf();
    dev.textContent = `${perf.fps}fps · chunks ${perf.chunkSprites} · nodes ${perf.nodes}`;
    requestAnimationFrame(refresh);
  }
  refresh();

  game.on('inv-changed', () => { if (consoleOpen()) renderTab[activeTab](); });
  game.on('stats-changed', () => { if (consoleOpen()) renderTab[activeTab](); });

  // keyboard: P → Hero, I → Bag, C → Craft, B → Dungeon, G → Guide, Esc close, Tab cycle while open
  window.addEventListener('keydown', (e) => {
    const k = e.key.toLowerCase();
    if (k === 'p') { e.preventDefault(); toggleConsole('hero'); }
    else if (k === 'i') { e.preventDefault(); toggleConsole('bag'); }
    else if (k === 'c') { e.preventDefault(); toggleConsole('craft'); }
    else if (k === 'b') { e.preventDefault(); toggleConsole('delve'); }
    else if (k === 'g') { e.preventDefault(); toggleConsole('guides'); }
    else if (e.key === 'Escape') closeConsole();
    else if (e.key === 'Tab' && consoleOpen()) {
      e.preventDefault();
      const i = TABS.indexOf(activeTab);
      showTab(TABS[(i + (e.shiftKey ? TABS.length - 1 : 1)) % TABS.length]);
    }
  });
}

function goalFor(game: Game): string {
  const s = game.snapshot();
  const inv = game.save.inventory;
  const wood = inv['wood'] ?? 0;
  const stone = inv['stone'] ?? 0;
  const builtCampfire = game.save.world.placed.some((p) => p.kind === 'campfire');
  const hasStoneTool = game.save.tools.axe >= 2 || game.save.tools.pickaxe >= 2;
  const enteredDungeon = s.deepest > 0 || s.mode === 'dungeon';
  // first-session guided path: wood → campfire → stone tools → dungeon (mirrors the DoD arc)
  if (wood < 5 && !builtCampfire && game.save.tools.axe === 0) return 'Goal: gather Wood — stand by a tree, tap ✋ / E';
  if (!builtCampfire) return 'Goal: craft a Campfire in 🔨, then place it (🎒)';
  if (!hasStoneTool && stone < 5) return 'Goal: mine Stone — tap ✋ / E by a grey rock';
  if (!hasStoneTool) return 'Goal: craft Stone tools in 🔨';
  if (!enteredDungeon) return 'Goal: seek a dark Dungeon door out in the world';
  if (s.foodBuffs === 0) return 'Tip: eat food (🎒) for a lasting buff';
  return 'Goal: descend deeper, grow stronger, light the grove';
}

function setupJoystick(joy: HTMLElement, nub: HTMLElement, game: Game): void {
  let pid: number | null = null; // the one pointer that owns the stick (multitouch-safe)
  let originX = 0, originY = 0;
  const R = 48, DEAD = 8; // max radius + dead zone (no twitch on tiny thumb moves)
  const set = (dx: number, dy: number) => {
    const d = Math.hypot(dx, dy);
    if (d < DEAD) { nub.style.transform = 'translate(0,0)'; game.intent.move = { x: 0, y: 0 }; return; }
    const ux = dx / d, uy = dy / d;
    const out = Math.min(1, (d - DEAD) / (R - DEAD)); // rescaled — full speed only near the edge
    const clamp = Math.min(d, R);
    nub.style.transform = `translate(${ux * clamp}px, ${uy * clamp}px)`;
    game.intent.move = { x: ux * out, y: uy * out };
  };
  joy.addEventListener('pointerdown', (e) => {
    if (pid !== null) return; // a second finger on the stick is ignored (belongs to a button)
    e.preventDefault(); e.stopPropagation();
    pid = e.pointerId;
    originX = e.clientX; originY = e.clientY; // dynamic origin: centres where the thumb lands
    try { joy.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
  });
  joy.addEventListener('pointermove', (e) => { if (e.pointerId !== pid) return; e.preventDefault(); set(e.clientX - originX, e.clientY - originY); });
  const end = (e: PointerEvent) => { if (e.pointerId !== pid) return; pid = null; nub.style.transform = 'translate(0,0)'; game.intent.move = { x: 0, y: 0 }; };
  joy.addEventListener('pointerup', end);
  joy.addEventListener('pointercancel', end);
}

function setupInstallHint(root: HTMLElement, game: Game): void {
  const seen = (() => { try { return localStorage.getItem('wg.installHint') === '1'; } catch { return true; } })();
  const dismiss = () => { try { localStorage.setItem('wg.installHint', '1'); } catch { /* private mode */ } };
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone;
  if (standalone || seen) return;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (isIOS) {
    // wait until the player has actually gathered something — an install prompt on an empty spawn
    // reads as noise; after first value it reads as "keep this progress"
    let shown = false;
    const show = () => {
      if (shown) return; shown = true;
      const b = el('div', 'wg-install clickable', '📲 Add to Home Screen (Share → Add) to keep your world saved <button class="wg-inst-x">✕</button>');
      root.appendChild(b);
      (b.querySelector('.wg-inst-x') as HTMLElement).onclick = () => { dismiss(); b.remove(); };
    };
    game.on('inv-changed', show);
  } else {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      const deferred = e as Event & { prompt: () => void };
      const b = el('div', 'wg-install clickable', '📲 Install Wildergrove <button class="wg-inst-go">Install</button><button class="wg-inst-x">✕</button>');
      root.appendChild(b);
      (b.querySelector('.wg-inst-go') as HTMLElement).onclick = () => { deferred.prompt(); dismiss(); b.remove(); };
      (b.querySelector('.wg-inst-x') as HTMLElement).onclick = () => { dismiss(); b.remove(); };
    });
  }
}

function buildGuide(root: HTMLElement, coarse: boolean, autoShow: boolean): void {
  const overlay = el('div', 'wg-guide clickable');
  const move = coarse ? 'the joystick' : 'W A S D';
  const gather = coarse ? 'the ✋ button' : 'E';
  const atk = coarse ? 'the ⚔️ button' : 'Space';
  overlay.innerHTML = `
    <div class="wg-guide-card">
      <h1>Wildergrove</h1>
      <p class="wg-sub">A cozy survival world with dark things below</p>
      <ul>
        <li><b>Move</b> with ${move}, <b>gather</b> with ${gather}, <b>fight</b> with ${atk}.</li>
        <li>Follow the <b>Goal</b> at the top — it always tells you what to do next.</li>
        <li>Food is a <b>buff</b> (never hunger), night is dangerous, and <b>death just sends you home</b>.</li>
      </ul>
      <p class="wg-tip">Full how-to anytime in the <b>📖 Guide</b> tab (press <b>G</b>).</p>
      <button class="wg-guide-go clickable">Enter the grove →</button>
    </div>`;
  root.appendChild(overlay);
  const markSeen = () => { try { localStorage.setItem('wg.seenGuide', '1'); } catch { /* private mode */ } };
  if (autoShow) root.classList.add('wg-guide-open'); // first session: shown → hide HUD chrome behind it
  else overlay.classList.add('wg-hidden');           // returning player: hidden, reopen with G
  (overlay.querySelector('.wg-guide-go') as HTMLElement).onclick = () => {
    overlay.classList.add('wg-hidden');
    root.classList.remove('wg-guide-open');
    markSeen(); // dismissing the welcome marks it seen (won't auto-show again)
  };
}

function injectStyles(): void {
  if (document.getElementById('wg-style')) return;
  const s = document.createElement('style');
  s.id = 'wg-style';
  s.textContent = `
  .wg-night{position:absolute;inset:0;background:#0a1430;opacity:0;transition:opacity 1s;pointer-events:none;mix-blend-mode:multiply}
  /* torchlit vision: only darkens the GAME (low z-index, under the HUD/controls below). */
  .wg-fog{position:absolute;inset:0;pointer-events:none;z-index:4;background:radial-gradient(circle at 50% 50%, rgba(3,2,6,0) 165px, rgba(3,2,6,0.3) 240px, rgba(3,2,6,0.78) 350px, rgba(3,2,6,0.95) 460px)}
  /* keep the HUD + controls above the dungeon fog so they're always readable */
  .wg-hud,.wg-tr,.wg-goal,.wg-hint,.wg-toasts,.wg-btn,.wg-dun,.wg-joy,.wg-dev{z-index:12}
  .wg-hud{position:absolute;top:10px;left:10px;display:flex;flex-direction:column;gap:6px;font-size:13px}
  .wg-bar-wrap{position:relative;width:180px;height:20px;background:#0006;border:1px solid #0008;border-radius:10px;overflow:hidden}
  .wg-bar{height:100%;transition:width .2s}
  .wg-hp{background:linear-gradient(90deg,#b23,#e55)}
  .wg-bar-text{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:11px;text-shadow:0 1px 2px #000}
  .wg-gold{background:#0006;padding:2px 8px;border-radius:8px;width:max-content}
  .wg-food{background:#0006;padding:2px 8px;border-radius:8px;width:max-content}
  .wg-fed{color:#8f8;margin-left:6px} .wg-unfed{color:#c99}
  .wg-safe{background:#2a5a3acc;padding:2px 8px;border-radius:8px;width:max-content;font-size:12px;color:#bfeecb}
  .wg-weapon{background:#0006;padding:2px 10px;border-radius:8px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;font-weight:600}
  .wg-weapon:hover{background:#0008}
  .wg-tr{position:absolute;top:10px;right:10px;display:flex;gap:10px;align-items:flex-start}
  .wg-widget{display:flex;flex-direction:column;align-items:center;gap:3px}
  .wg-cap{font-size:9px;letter-spacing:.5px;text-transform:uppercase;opacity:.65}
  .wg-clock,.wg-compass,.wg-widget-btn{width:44px;height:44px;border-radius:50%;box-shadow:0 2px 6px #0006,inset 0 0 0 2px #0005;position:relative;overflow:hidden}
  .wg-clock{background:linear-gradient(#8fc7f0,#cfe6ff 55%,#7a6a4a 55%,#5a4a30);transition:background .8s}
  .wg-clock.wg-is-night{background:linear-gradient(#0a1436,#1a2a55 55%,#20304a 55%,#151f30)}
  .wg-clock-horizon{position:absolute;left:0;right:0;top:55%;height:1px;background:#0004}
  .wg-orb{position:absolute;font-size:16px;line-height:16px}
  .wg-compass{background:radial-gradient(#26331c,#161f10);overflow:visible}
  .wg-compass span{position:absolute;font-size:8px;font-weight:700;opacity:.7}
  .wg-cn{top:2px;left:50%;transform:translateX(-50%);color:#e88}
  .wg-cs{bottom:2px;left:50%;transform:translateX(-50%)}
  .wg-ce{right:3px;top:50%;transform:translateY(-50%)}
  .wg-cw{left:3px;top:50%;transform:translateY(-50%)}
  .wg-comp-arrow{position:absolute;top:50%;left:50%;margin:-9px 0 0 -6px;font-size:16px;line-height:1;color:#e8c060;transform-origin:center;transition:transform .15s}
  .wg-widget-btn{border:none;background:#3a4a2acc;color:#f2ead9;font-size:22px;cursor:pointer;overflow:visible}
  .wg-goal{position:absolute;top:10px;left:50%;transform:translateX(-50%);background:#2a3a1ecc;padding:4px 14px;border-radius:8px;font-size:13px}
  .wg-hint{position:absolute;bottom:14px;left:50%;transform:translateX(-50%);background:#0007;padding:4px 12px;border-radius:8px;font-size:12px;opacity:.85}
  .wg-toasts{position:absolute;top:80px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;gap:4px;align-items:center}
  .wg-toast{background:#1a2a14dd;padding:4px 12px;border-radius:8px;font-size:13px;transition:opacity .4s,transform .4s}
  .wg-toast-out{opacity:0;transform:translateY(-8px)}
  .wg-btn{position:absolute;width:48px;height:48px;border-radius:12px;border:1px solid #0008;background:#2a3a1ecc;color:#f2ead9;font-size:22px;cursor:pointer}
  .wg-menu-btn{top:12px;right:124px;font-size:24px}
  .wg-console{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#1a2414f7;border:1px solid #0008;border-radius:16px;width:min(94vw,440px);height:min(84vh,560px);display:flex;flex-direction:column;overflow:hidden;z-index:40;box-shadow:0 12px 44px #000b}
  .wg-con-head{display:flex;align-items:center;justify-content:space-between;padding:8px 10px;border-bottom:1px solid #0006;background:#141c0e;gap:6px}
  .wg-con-tabs{display:flex;gap:4px;flex-wrap:wrap}
  .wg-con-tab{padding:7px 11px;border:none;border-radius:9px;background:#0004;color:#c6d2ba;cursor:pointer;font-size:13px;font-weight:600}
  .wg-con-tab.wg-tab-on{background:#4a6a2a;color:#f2ead9}
  .wg-con-close{border:none;background:#0004;color:#f2ead9;width:30px;height:30px;border-radius:8px;cursor:pointer;font-size:15px;flex-shrink:0}
  .wg-con-body{padding:14px;flex:1;min-height:0;overflow-y:auto}
  .wg-cat-chips{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:10px}
  .wg-chip{padding:5px 10px;border:none;border-radius:20px;background:#0004;color:#c6d2ba;cursor:pointer;font-size:12px}
  .wg-chip-on{background:#5a4a2a;color:#f2ead9;outline:1px solid #b0904a}
  .wg-delve-hero{font-size:15px;margin-bottom:6px;color:#bca0e0}
  .wg-delve-sec{font-weight:600;margin:12px 0 6px;font-size:13px;opacity:.9}
  .wg-bests{display:flex;flex-direction:column;gap:3px}
  .wg-best-row{display:flex;justify-content:space-between;background:#0003;padding:4px 10px;border-radius:6px;font-size:13px}
  .wg-bless-info{font-size:13px;background:#0003;padding:6px 10px;border-radius:8px}
  .wg-claim-bless{margin-top:8px;width:100%;padding:10px;border:none;border-radius:10px;background:#7a4a8a;color:#fff;cursor:pointer;font-size:14px}
  .wg-menu-alert{outline:2px solid #d78ae0;outline-offset:1px;animation:wg-pulse 1.2s infinite}
  @keyframes wg-pulse{50%{outline-color:#f6c0fb}}
  .wg-set-row{display:flex;align-items:center;justify-content:space-between;gap:10px;margin:8px 0;font-size:14px}
  .wg-set-row button{padding:6px 12px;border:none;border-radius:8px;background:#3a4a2a;color:#f2ead9;cursor:pointer;font-size:13px}
  .wg-set-note{font-size:11px;opacity:.65;margin:6px 0 4px;line-height:1.4}
  .wg-ver{font-size:12px;opacity:.6;font-variant-numeric:tabular-nums}
  .wg-account{background:#101a2699;border:1px solid #3a5a7a55;border-radius:10px;padding:9px 11px;margin:8px 0}
  .wg-acc-head{font-size:12px;font-weight:600;color:#9fc8e0;margin-bottom:6px}
  .wg-acc-opt{font-weight:400;opacity:.6}
  .wg-acc-who{font-size:13px;margin-bottom:4px}
  .wg-acc-status{font-size:11px;opacity:.7;margin-top:5px}
  .wg-account input{width:100%;box-sizing:border-box;margin:4px 0;padding:8px 10px;border-radius:8px;border:1px solid #0007;background:#0005;color:#f2ead9;font-size:13px}
  .wg-acc-btns{display:flex;gap:8px;margin-top:6px}
  .wg-acc-btns button{flex:1;padding:8px;border:none;border-radius:8px;background:#3a5a7a;color:#f2ead9;cursor:pointer;font-size:13px}
  .wg-feedback{background:#5a4a7a!important}
  .wg-cl-badge{display:inline-block;margin-left:3px;color:#f0a030;font-size:16px;line-height:0;vertical-align:middle}
  .wg-cl-entry{margin:2px 0 8px}
  .wg-cl-ver{font-weight:700;color:#cdb0f0;font-size:13px}
  .wg-cl-date{font-weight:400;opacity:.55;font-size:11px;margin-left:4px}
  .wg-cx-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(84px,1fr));gap:6px}
  .wg-cx-cell{display:flex;flex-direction:column;align-items:center;gap:2px;padding:8px 4px;border-radius:9px;background:#0004;text-align:center}
  .wg-cx-ico{font-size:20px;line-height:1}
  .wg-cx-name{font-size:10px;opacity:.85;line-height:1.15}
  .wg-cx-locked{opacity:.4}
  .wg-cx-locked .wg-cx-name{font-style:italic}
  .wg-import-box{width:100%;height:60px;margin:6px 0;border-radius:8px;border:1px solid #0007;background:#0005;color:#f2ead9;padding:6px;font-size:11px;resize:vertical;font-family:monospace}
  .wg-import{width:100%;padding:8px;border:none;border-radius:8px;background:#4a6a2a;color:#f2ead9;cursor:pointer;font-size:13px}
  .wg-narr{position:absolute;top:58%;left:50%;transform:translateX(-50%);max-width:min(88vw,460px);text-align:center;background:#0d0a12e8;border:1px solid #4a3a5a;border-radius:14px;padding:14px 20px;font-size:16px;font-style:italic;line-height:1.45;color:#e6dcc6;box-shadow:0 6px 24px #000a;transition:opacity .8s;pointer-events:none;z-index:60}
  .wg-narr-out{opacity:0}
  .wg-flash{position:absolute;inset:0;pointer-events:none;opacity:0;z-index:64;mix-blend-mode:screen}
  .wg-act{bottom:30px;right:30px;width:64px;height:64px;font-size:26px}
  .wg-act-lbl{position:absolute;top:3px;right:5px;font-size:9px;opacity:.7;text-transform:uppercase}
  .wg-near{font-size:12px;margin-bottom:8px;opacity:.9}
  .wg-dim{opacity:.6}
  .wg-craft-list{display:flex;flex-direction:column;gap:5px}
  .wg-recipe{display:flex;align-items:center;gap:8px;background:#0003;border-radius:8px;padding:6px 8px;font-size:13px;opacity:.55}
  .wg-recipe.wg-can{opacity:1;outline:1px solid #6a3;cursor:pointer}
  .wg-recipe.wg-can:hover{background:#0005}
  .wg-r-ic{font-size:18px} .wg-r-nm{flex:1} .wg-r-need{font-size:11px;display:flex;gap:6px;flex-wrap:wrap}
  .wg-ok{color:#8f8} .wg-miss{color:#e88}
  .wg-atk{bottom:104px;right:56px;width:72px;height:72px;font-size:30px;background:#5a2a2acc}
  .wg-panel{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#1a2414f2;border:1px solid #0008;border-radius:14px;padding:16px;width:min(90vw,360px);max-height:80vh;overflow:auto}
  .wg-panel h2{margin-bottom:10px}
  .wg-inv-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .wg-cell{display:flex;flex-direction:column;align-items:center;background:#0004;border-radius:10px;padding:8px;position:relative}
  .wg-eatable{outline:1px solid #6a3;cursor:pointer}
  .wg-cell .wg-ic{font-size:26px} .wg-cell .wg-nm{font-size:11px;opacity:.8} .wg-cell .wg-ct{font-weight:700}
  .wg-empty{grid-column:1/3;text-align:center;opacity:.6;padding:16px}
  .wg-stats{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;margin-bottom:10px;font-size:14px;background:#0004;padding:6px;border-radius:8px}
  .wg-tab-hero-btn{display:flex;align-items:center;gap:5px;padding-left:5px!important}
  .wg-hero-name{max-width:100px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .wg-tabface{width:22px;height:22px;border-radius:6px;background:#2a3a1e;border:1.5px solid #7a5a34;flex-shrink:0}
  .wg-hero-top{display:flex;gap:12px;align-items:center;margin-bottom:10px}
  .wg-doll{flex-shrink:0;display:flex;align-items:center;justify-content:center;padding:6px;background:radial-gradient(circle at 50% 38%,#3a4a2a,#1e2814);border:2px solid #7a5a34;border-radius:12px;box-shadow:inset 0 2px 8px #0006}
  .wg-doll-face{width:84px;height:75px}
  .wg-hero-top .wg-stats{flex:1;margin-bottom:0}
  .wg-slotgrid{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px}
  .wg-relicrow{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px}
  .wg-slot{display:flex;align-items:center;gap:8px;background:#0003;border:1px solid #ffffff14;border-radius:9px;padding:6px 8px;cursor:pointer;min-width:0}
  .wg-slot:hover{background:#0005}
  .wg-slot.wg-slot-on{background:#2a3a1e66;border-color:#6a3}
  .wg-slot.wg-slot-sel{border-color:#cdb87a;box-shadow:0 0 0 1px #cdb87a}
  .wg-relic.wg-slot-on{background:#2a2a4a66;border-color:#89a}
  .wg-slot-ic{font-size:19px;flex-shrink:0}
  .wg-slot-body{display:flex;flex-direction:column;min-width:0;line-height:1.25}
  .wg-slot-lbl{font-size:9.5px;letter-spacing:.4px;text-transform:uppercase;opacity:.55}
  .wg-slot-nm{font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .wg-grove{background:#0f1a0d99;border:1px solid #3a7a4a55;border-radius:10px;padding:8px 10px;margin-bottom:8px}
  .wg-grove-head{font-size:12px;font-weight:600;color:#9fe0a8;margin-bottom:5px}
  .wg-grove-boon{font-weight:400;color:#c8e6c0;opacity:.85}
  .wg-grove-bar{height:7px;border-radius:5px;background:#0006;overflow:hidden}
  .wg-grove-fill{height:100%;background:linear-gradient(90deg,#4a8a3a,#8fd86a);border-radius:5px;transition:width .5s ease}
  .wg-grove-sub{font-size:10.5px;opacity:.7;margin-top:5px}
  .wg-passives{background:#0f130a99;border:1px solid #ffffff10;border-radius:10px;padding:7px 9px;margin-bottom:8px;display:flex;flex-direction:column;gap:3px}
  .wg-passives-head{font-size:9.5px;letter-spacing:.5px;text-transform:uppercase;opacity:.5;margin-bottom:1px}
  .wg-passive-row{display:flex;align-items:center;gap:7px;font-size:12px;opacity:.92}
  .wg-passive-ic{flex-shrink:0}
  .wg-picker{background:#0e140a;border:1px solid #3a4a2a;border-radius:10px;padding:8px;margin-bottom:8px;display:flex;flex-direction:column;gap:4px}
  .wg-picker-head{font-weight:600;font-size:12px;opacity:.85;margin-bottom:2px}
  .wg-pick-row{display:flex;align-items:center;gap:7px;background:#0004;border-radius:7px;padding:6px 8px;font-size:12.5px;cursor:pointer}
  .wg-pick-row:hover{background:#0006}
  .wg-pick-row.wg-eq{background:#2a3a1e88;outline:1px solid #6a3}
  .wg-pick-off{justify-content:center;color:#e0a0a0;font-weight:600;background:#3a1e1e77}
  .wg-pick-nm{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .wg-pick-sum{font-size:11px;opacity:.9;white-space:nowrap;font-weight:600}
  .wg-pick-even{opacity:.45;font-weight:400}
  .wg-pick-eqbadge{color:#8fd07a;font-weight:700}
  .wg-gear-foot{display:flex;justify-content:flex-end;margin-top:2px}
  .wg-salvage{padding:5px 11px;border:none;border-radius:8px;background:#4a3a2a;color:#f2ead9;cursor:pointer;font-size:12px}
  .wg-g-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0}
  .wg-emp{border:none;border-radius:6px;background:#5a4a2a;color:#f2ead9;cursor:pointer;font-size:13px;padding:2px 7px}
  .wg-tier{display:flex;align-items:center;gap:6px;justify-content:center;font-size:12px;margin-bottom:8px;opacity:.9}
  .wg-tier-btns{display:inline-flex;gap:8px}
  .wg-rarity-legend{display:flex;flex-wrap:wrap;gap:6px 12px;margin:2px 0 6px}
  .wg-rar-chip{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600}
  .wg-rar-note{font-size:11px;line-height:1.45;margin-bottom:4px}
  .wg-guides-intro{font-size:12.5px;line-height:1.5;opacity:.85;margin-bottom:10px}
  .wg-gd{background:#0003;border:1px solid #ffffff12;border-radius:10px;margin-bottom:6px;overflow:hidden}
  .wg-gd summary{cursor:pointer;padding:9px 11px;font-weight:600;font-size:13.5px;list-style:none;user-select:none}
  .wg-gd summary::-webkit-details-marker{display:none}
  .wg-gd summary::after{content:'▸';float:right;opacity:.5;transition:transform .15s}
  .wg-gd[open] summary::after{transform:rotate(90deg)}
  .wg-gd-body{padding:0 12px 10px}
  .wg-gd-body ul{margin:4px 0 0 16px;display:flex;flex-direction:column;gap:6px;font-size:12.5px;line-height:1.5}
  .wg-tier-b{width:30px;height:30px;border:1px solid #0006;border-radius:8px;background:#0004;color:#f2ead9;cursor:pointer;font-size:14px}
  .wg-tier-on{background:#5a3a6a;outline:1px solid #b090e0}
  .wg-blessbtn{position:absolute;top:128px;left:10px;padding:7px 12px;border:none;border-radius:10px;background:#7a4a8acc;color:#fff;cursor:pointer;font-size:13px;box-shadow:0 2px 6px #0006;z-index:20}
  .wg-death{position:absolute;inset:0;background:#0a0610d8;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(2px);z-index:70}
  .wg-death-card{background:#1a1018;border:1px solid #5a2a3a;border-radius:16px;padding:24px;width:min(90vw,360px);text-align:center}
  .wg-death-card h2{font-size:24px;color:#e0708a;margin-bottom:10px}
  .wg-death-msg{font-size:14px;line-height:1.5;opacity:.9;margin-bottom:18px}
  .wg-death-safe{display:block;margin-top:12px;padding:9px 11px;background:#2a3a1e88;border:1px solid #6a3;border-radius:9px;color:#dff0c8;font-size:13px;line-height:1.45;opacity:1}
  .wg-death-ok{width:100%;padding:12px;border:none;border-radius:10px;background:#5a3a4a;color:#f2ead9;font-size:15px;cursor:pointer}
  .wg-death-ok:hover{background:#6a4a5a}
  .wg-portal{position:absolute;inset:0;background:#06101099;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(2px);z-index:70}
  .wg-portal-card{background:#0e1a1e;border:1px solid #2f8a92;border-radius:16px;padding:24px;width:min(90vw,360px);text-align:center;box-shadow:0 0 30px #2fb0b855}
  .wg-portal-swirl{font-size:40px;animation:wg-spin 3s linear infinite}
  @keyframes wg-spin{to{transform:rotate(360deg)}}
  .wg-portal-card h2{font-size:22px;color:#6fe0e0;margin:8px 0 6px}
  .wg-portal-card p{font-size:14px;opacity:.9;margin-bottom:18px}
  .wg-portal-btns{display:flex;flex-direction:column;gap:8px}
  .wg-portal-go{padding:12px;border:none;border-radius:10px;background:#2f8a92;color:#f2ead9;font-size:15px;cursor:pointer}
  .wg-portal-go:hover{background:#3fa0a8}
  .wg-portal-no{padding:10px;border:none;border-radius:10px;background:#2a3436;color:#c8d4d4;font-size:14px;cursor:pointer}
  .wg-done{position:absolute;inset:0;background:#0a0610cc;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(2px);z-index:70}
  .wg-done-card{background:#1a1424;border:1px solid #7a5a3a;border-radius:16px;padding:24px;width:min(90vw,380px);text-align:center;box-shadow:0 0 30px #f0a03044}
  .wg-done-star{font-size:40px}
  .wg-done-card h2{font-size:24px;color:#f0c860;margin:6px 0 8px}
  .wg-done-msg{font-size:14px;opacity:.9;margin-bottom:18px;line-height:1.5}
  .wg-done-btns{display:flex;flex-direction:column;gap:8px}
  .wg-done-share{padding:11px;border:none;border-radius:10px;background:#5a4a7a;color:#f2ead9;font-size:14px;cursor:pointer}
  .wg-done-explore{padding:12px;border:none;border-radius:10px;background:#4a6a2a;color:#f2ead9;font-size:15px;cursor:pointer}
  .wg-done-exit{padding:11px;border:none;border-radius:10px;background:#7a3a2a;color:#f2ead9;font-size:14px;cursor:pointer}
  .wg-install{position:absolute;bottom:calc(8px + env(safe-area-inset-bottom));left:50%;transform:translateX(-50%);background:#1a2414f0;border:1px solid #3a4a2a;border-radius:10px;padding:8px 12px;font-size:12px;max-width:92vw;display:flex;align-items:center;gap:8px;z-index:50}
  .wg-panel-open .wg-install{display:none}
  .wg-inst-go{border:none;border-radius:7px;background:#4a6a2a;color:#fff;padding:5px 10px;cursor:pointer}
  .wg-inst-x{border:none;background:none;color:#f2ead9;opacity:.7;cursor:pointer;font-size:14px}
  .wg-close{margin-top:12px;width:100%;padding:8px;border-radius:8px;border:none;background:#3a4a2a;color:#f2ead9;cursor:pointer}
  .wg-joy{position:absolute;bottom:30px;left:30px;width:110px;height:110px;border-radius:50%;background:#fff2;border:1px solid #fff3;touch-action:none}
  .wg-joy-nub{position:absolute;top:50%;left:50%;width:44px;height:44px;margin:-22px;border-radius:50%;background:#fff6}
  .wg-dev{position:absolute;bottom:4px;left:50%;transform:translateX(-50%);font-size:10px;opacity:.4;font-family:monospace}
  .wg-guide{position:absolute;inset:0;background:#0a0e07f2;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(2px);z-index:90}
  /* while the welcome/how-to-play guide is open it's a clean modal — hide all HUD chrome behind it */
  .wg-guide-open .wg-hud,.wg-guide-open .wg-tr,.wg-guide-open .wg-goal,.wg-guide-open .wg-hint,.wg-guide-open .wg-install,.wg-guide-open .wg-dev,.wg-guide-open .wg-joy,.wg-guide-open .wg-btn{display:none}
  .wg-guide-card{background:#1a2414;border:1px solid #3a4a2a;border-radius:16px;padding:22px;width:min(92vw,420px);max-height:88vh;overflow:auto}
  .wg-guide-card h1{font-size:26px;color:#cdb87a}
  .wg-guide-card .wg-sub{opacity:.7;margin-bottom:12px;font-size:13px}
  .wg-guide-card ul{margin:0 0 12px 18px;display:flex;flex-direction:column;gap:8px;font-size:14px;line-height:1.4}
  .wg-guide-card .wg-tip{font-size:12px;opacity:.75;margin-bottom:14px}
  .wg-guide-go{width:100%;padding:12px;border:none;border-radius:10px;background:#4a6a2a;color:#f2ead9;font-size:15px;cursor:pointer}
  .wg-dun{position:absolute;top:10px;left:50%;transform:translateX(-50%);display:flex;gap:10px;align-items:center;background:#14101ecc;padding:6px 12px;border-radius:10px;border:1px solid #3a3450}
  .wg-dun-floor{font-weight:700;color:#bca0e0}
  .wg-dun-satchel{font-size:13px;opacity:.9}
  .wg-dun-leave{padding:4px 10px;border:none;border-radius:8px;background:#3a4a2a;color:#f2ead9;cursor:pointer;font-size:12px}
  .wg-bless{position:absolute;top:57%;left:50%;transform:translateX(-50%);background:#1a1424f2;border:1px solid #6a4a8a;border-radius:14px;padding:12px 14px;text-align:center;max-width:min(94vw,400px);z-index:60}
  .wg-bless h3{margin-bottom:4px;color:#cdb0f0}
  .wg-bless-sub{font-size:12.5px;opacity:.8;margin-bottom:10px;font-style:italic}
  .wg-bless-row{display:flex;gap:10px;justify-content:center}
  .wg-bless-card{padding:13px 12px;border:1px solid #6a4a8a;border-radius:10px;background:#2a2040;color:#f2ead9;cursor:pointer;font-size:14px;min-width:86px}
  .wg-bless-card:hover{background:#3a2a55}
  .wg-hidden{display:none!important}

  /* ---- touch / phone layout: fires in BOTH orientations (pointer:coarse), nothing overlaps,
         and controls respect the notch / home indicator (safe-area insets) ---- */
  @media (pointer: coarse), (max-width: 820px) {
    .wg-hud{font-size:12px;gap:4px;top:calc(10px + env(safe-area-inset-top));left:calc(10px + env(safe-area-inset-left))}
    .wg-bar-wrap{width:120px;height:18px}
    .wg-gold,.wg-food,.wg-weapon{padding:1px 8px;font-size:12px}
    /* single .wg-tr rule so the notch inset isn't clobbered by a later same-specificity one */
    .wg-tr{top:8px;right:calc(8px + env(safe-area-inset-right));gap:6px}
    .wg-clock,.wg-compass,.wg-widget-btn{width:36px;height:36px}
    .wg-cap{font-size:8px}
    .wg-orb{font-size:12px}
    .wg-comp-arrow{font-size:13px;margin:-7px 0 0 -5px}
    .wg-menu-btn{top:8px;right:92px;width:40px;height:40px;font-size:22px}
    /* mobile: the console is a bottom sheet (game stays visible up top) */
    .wg-console{top:auto;bottom:0;left:0;transform:none;width:100%;height:68vh;border-radius:16px 16px 0 0;z-index:45}
    .wg-con-tab{padding:8px 12px;font-size:14px}
    .wg-narr{font-size:15px;padding:14px 16px}
    .wg-goal{top:112px;font-size:12px;max-width:92vw;text-align:center}
    .wg-toasts{top:150px}
    .wg-hint{bottom:215px;font-size:11px;max-width:84vw;text-align:center}
    .wg-dev{display:none}
    .wg-joy{bottom:calc(26px + env(safe-area-inset-bottom));left:calc(22px + env(safe-area-inset-left));width:104px;height:104px}
    .wg-joy-nub{width:42px;height:42px;margin:-21px}
    .wg-atk{bottom:calc(104px + env(safe-area-inset-bottom));right:calc(26px + env(safe-area-inset-right));width:66px;height:66px;font-size:28px}
    .wg-act{bottom:calc(26px + env(safe-area-inset-bottom));right:calc(30px + env(safe-area-inset-right));width:58px;height:58px;font-size:23px}
    .wg-panel{width:min(94vw,420px);max-height:86vh}
    .wg-close{padding:12px}
    .wg-guide-card{padding:18px}
  }
  /* landscape phones are short — pull the hint clear of the controls */
  @media (max-height: 480px) { .wg-hint{bottom:150px} .wg-goal{top:70px} .wg-toasts{top:96px} }
  /* short viewports: a top:% blessing card climbs into the (centred) hero — dock it to the bottom
     and compact it so the hero stays clear above the prompt (the owner's "notice below the hero" rule) */
  @media (max-height: 560px) {
    .wg-bless{top:auto;bottom:calc(10px + env(safe-area-inset-bottom));padding:9px 12px;max-width:min(96vw,440px)}
    .wg-bless h3{font-size:14px;margin-bottom:2px}
    .wg-bless-sub{font-size:11px;margin-bottom:7px}
    .wg-bless-card{padding:9px 12px;font-size:13px;min-width:76px}
  }
  `;
  document.head.appendChild(s);
}
